<template>
  <v-container fluid pt-0>
    <v-row align="center" justify="end">
      <v-col sm="9">
        <v-row>
          <v-col class="py-0">
            <date-range-picker
              :gt-date-from-string="validFromFilter"
              :gt-date-to-string="validToFilter"
              @onRangeSelected="onValidDatesRangeSelected"
              :outlined="outlinedInputs"
              customDateFromLabel="Valid From"
              customDateToLabel="Valid To"
            ></date-range-picker>
          </v-col>
          <v-col class="py-0">
            <date-range-picker
              :gt-date-from-string="dateFromFilter"
              :gt-date-to-string="dateToFilter"
              @onRangeSelected="onDateRangeSelected"
              :outlined="outlinedInputs"
              customDateFromLabel="Book Before Date from"
              customDateToLabel="Book Before Date to"
            ></date-range-picker>
          </v-col>
          <v-col class="py-0">
            <gt-multi-select
              label="Services"
              :items="services"
              select-all
              v-model="servicesFilter"
              item-text="node.name"
              item-value="node.id"
              :show-selected="2"
              :outlined="outlinedInputs"
            ></gt-multi-select>
          </v-col>
        </v-row>
      </v-col>
      <v-col sm="3">
        <v-select
          v-if="showGroupedView"
          :items="groupByOptions"
          v-model="selectedGroupedBy"
          label="Group by"
          item-text="name"
          return-object
          :outlined="outlinedInputs"
          hide-details
        ></v-select>
      </v-col>
      <v-switch
        prepend-icon="mdi-view-list"
        class="mr-3"
        label="Group View"
        v-model="showGroupedView"
        @change="onShowGroupedViewChange"
      ></v-switch>
      <div class="pr-2">
        <list-visible-columns-and-filter
          :visible-columns="visibleColumns"
          :selectable-headers="selectableHeaders"
          v-model="search"
          @change="onVisibleColumnsChange"
        ></list-visible-columns-and-filter>
      </div>
      <add-discounts-dialog :services="services" @create="onDiscountCreate">
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            color="primary"
            :disabled="$store.getters.hasAccessTo('suppliers:view_only')"
          >
            <v-icon left>mdi-plus-circle</v-icon>Add Discounts
          </v-btn>
        </template>
      </add-discounts-dialog>
    </v-row>

    <v-row v-if="showGroupedView">
      <v-col class="pa-0">
        <list-grouped :group="selectedGroupedBy" :items="discounts">
          <template v-slot:table="{ items }">
            <v-card outlined>
              <discounts-table
                :items="items"
                :headers="filteredHeaders"
                :search="search"
                :hide-footer="true"
                @delete="onDiscountDelete"
                @edit="onDiscountEdit"
              ></discounts-table>
            </v-card>
          </template>
        </list-grouped>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col class="pa-0">
        <v-card outlined>
          <discounts-table
            :items="discounts"
            :headers="filteredHeaders"
            :search="search"
            @delete="onDiscountDelete"
            @edit="onDiscountEdit"
          ></discounts-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="debug">
      <pre>{{ filter }}</pre>
    </v-row>

    <discount-item-editor
      v-model="editedItem"
      :show="showDiscountItemEditor"
      @input="onDiscountItemSave"
      @close="showDiscountItemEditor = false"
      :services="services"
    />
  </v-container>
</template>

<script>
import DiscountsTable from "./DiscountsTable";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import AddDiscountsDialog from "./AddDiscountsDialog";
import DiscountItemEditor from "./DiscountItemEditor";
import ListGrouped from "../../shared/ListGrouped";
import { ListMixin } from "../../../../mixins/ListMixin";
import { removeGQLTypename, sortByDate } from "../../../../helpers/index";
import DateRangePicker from "../../shared/DatesRangePicker.vue";
import GtMultiSelect from "../../shared/GtMultiSelect";
const moment = require("moment");
import gql from "graphql-tag";

export const UPDATE_DISCOUNT_MUTATION = gql`
  mutation updateDiscount($id: ID!, $discountInput: DiscountInput) {
    updateDiscount(id: $id, discountInput: $discountInput) {
      id
    }
  }
`;
export const DELETE_DISCOUNT_MUTATION = gql`
  mutation deleteDiscount($id: ID!) {
    deleteDiscount(id: $id) {
      id
    }
  }
`;
export const ADD_DISCOUNTS_MUTATION = gql`
  mutation addDiscounts(
    $supplierId: ID!
    $supplierAddDiscountInput: SupplierAddDiscountsInput
  ) {
    supplierAddDiscounts(
      supplierId: $supplierId
      supplierAddDiscountInput: $supplierAddDiscountInput
    ) {
      success
      error
    }
  }
`;
const QUERY = gql`
  query supplierDiscountsList($supplierId: ID!, $filter: SupplierDiscountFilterType) {
    supplier(id: $supplierId) {
      id
      discounts(filter: $filter) {
        totalCount
        edges {
          node {
            id
            description
            validFrom
            validTo
            bookBeforeDate
            advanceDays
            minBook
            free
            percent
            agentGroupName
            groupId
            round
            service {
              id
              name
            }
          }
        }
      }
    }
  }
`;
export default {
  name: "supplier-discounts",

  props: {
    showTitle: {
      type: Boolean,
      default: () => true,
    },
    supplierId: {
      type: String,
      default: "",
    },
    services: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    DiscountsTable,
    ListVisibleColumnsAndFilter,
    ListGrouped,
    AddDiscountsDialog,
    DiscountItemEditor,
    DateRangePicker,
    GtMultiSelect,
  },

  mixins: [ListMixin],

  data() {
    return {
      debug: false,
      showGroupedView: this.$store.getters.localSettings(
        `${this.$options.name}-showGroupedView`,
        false
      ),
      showDiscountItemEditor: false,
      search: "",
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "description",
        "validFrom",
        "validTo",
        "agentName",
      ]),
      editedItem: null,
      headers: [
        {
          text: "Description",
          sortable: true,
          value: "description",
          name: "description",
        },
        {
          text: "Valid From",
          sortable: true,
          value: "validFrom",
          name: "validFrom",
          sort: sortByDate,
        },
        {
          text: "Valid To",
          sortable: true,
          value: "validTo",
          name: "validTo",
          sort: sortByDate,
        },
        {
          text: "Book Before Date",
          sortable: true,
          value: "bookBeforeDate",
          name: "bookBeforeDate",
        },
        {
          text: "Advance Days",
          sortable: true,
          value: "advanceDays",
          name: "advanceDays",
        },
        {
          text: "Book (min duration)",
          sortable: true,
          value: "minBook",
          name: "minBook",
        },
        {
          text: "Free",
          sortable: true,
          value: "free",
          name: "free",
        },
        {
          text: "% Discount",
          sortable: true,
          value: "percent",
          name: "percent",
        },
        {
          text: "Agent Group",
          sortable: true,
          value: "agentGroupName",
          name: "agentGrupName",
        },
        {
          text: "Service",
          sortable: true,
          value: "service.name",
          name: "serviceName",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
      selectedGroupedBy: {
        name: "Service",
        getKey: (item) => item.service.name,
        getLabel: (item) => item.service.name,
      },
      groupByOptions: [
        {
          name: "Service",
          getKey: (item) => item.service.name,
          getLabel: (item) => item.service.name,
        },
        {
          name: "Description",
          getKey: (item) => item.description,
          getLabel: (item) => item.description,
        },
        {
          name: "Valid From",
          getKey: (item) => item.validFrom,
          getLabel: (item) =>
            `Valid from: ${this.$options.filters.formatDate(item.validFrom)}`,
        },
        {
          name: "Valid From - Valid To",
          getKey: (item) => `${item.validFrom}-${item.validTo}`,
          getLabel: (item) =>
            `Valid from: ${this.$options.filters.formatDate(
              item.validFrom
            )}, to: ${this.$options.filters.formatDate(item.validTo)}`,
        },
        {
          name: "Book Before Date",
          getKey: (item) => item.bookBeforeDate,
          getLabel: (item) =>
            `Book before: ${this.$options.filters.formatDate(
              item.bookBeforeDate
            )}`,
        },
      ],
      validFromFilter: moment().subtract(1, "year").toISOString(true),
      validToFilter: null,
      dateFromFilter: null,
      dateToFilter: null,
      servicesFilter: [],
    };
  },
  methods: {
    onValidDatesRangeSelected({ dateFrom: validFrom, dateTo: validTo }) {
      this.validFromFilter = validFrom;
      this.validToFilter = validTo;
    },
    onDateRangeSelected({ dateFrom, dateTo }) {
      this.dateFromFilter = dateFrom;
      this.dateToFilter = dateTo;
    },
    onShowGroupedViewChange(event) {
      this.$store.dispatch("updateLocalSettings", {
        [`${this.$options.name}-showGroupedView`]: event,
      });
    },
    onDiscountEdit(data) {
      this.editedItem = { ...data };
      this.showDiscountItemEditor = true;
    },
    async onDiscountItemSave(payload) {
      this.showDiscountItemEditor = false;
      try {
        const result = await this.$apollo.mutate({
          mutation: UPDATE_DISCOUNT_MUTATION,
          variables: {
            id: this.editedItem.id,
            discountInput: {
              minBook: parseInt(this.editedItem.minBook),
              groupId: payload.groupId,
              free: parseInt(this.editedItem.free),
              percent: parseFloat(this.editedItem.percent),
              round: payload.round,
              validFrom: payload.validFrom,
              validTo: payload.validTo,
              description: payload.description,
              bookBeforeDate: payload.bookBeforeDate,
              advanceDays: parseInt(payload.advanceDays),
            },
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Discount updated",
        });
        console.log(result);
      } catch (error) {
        console.log(error);
      }
    },
    async onDiscountDelete(data) {
      try {
        const result = await this.$apollo.mutate({
          mutation: DELETE_DISCOUNT_MUTATION,
          variables: {
            id: data.id,
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Discount removed",
        });
        console.log(result);
      } catch (error) {
        console.log(error);
      }
    },
    async onDiscountCreate(data) {
      try {
        const result = await this.$apollo.mutate({
          mutation: ADD_DISCOUNTS_MUTATION,
          variables: {
            supplierId: this.supplierId,
            supplierAddDiscountInput: data,
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Discount added",
        });
        console.log(result);
      } catch (error) {
        console.log(error);
      }
    },
  },
  computed: {
    filter() {
      return {
        validFrom_lte: this.validToFilter,
        validTo_gte: this.validFromFilter,
        services: [...this.servicesFilter],
      };
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    discounts() {
      return this.supplier && this.supplier.discounts
        ? [...this.supplier.discounts.edges.map(removeGQLTypename)]
        : [];
    },
  },
  apollo: {
    supplier: {
      query: QUERY,
      variables() {
        return {
          supplierId: this.supplierId,
          filter: { ...this.filter },
        };
      },
      fetchPolicy: "network-only",
      skip() {
        return !this.supplierId;
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/suppliers/discounts/SupplierDiscounts.vue